import React from 'react';
import { get, defaultTo } from 'lodash-es';
import { pageDefinition } from 'site-modules/shared/pages/page';
import { VehiclePreloader } from 'site-modules/shared/utils/core-page/vehicle-preloader-v2';
import { VehicleModel, buildVehiclePath, buildMakeModelDefaultYear } from 'client/data/models/vehicle-v2';
import { buildSubmodelsPath, VehicleGraphQLModel } from 'client/data/models/vehicle-graphql';
import { getModelState } from 'client/data/luckdragon/model';
import { buildCoreBreadcrumbs, getUrlPathFromParams } from 'site-modules/shared/utils/core-breadcrumbs-builder';
import { LinkWidgetsModel, getPageLinksPath } from 'client/data/models/link-widgets';
import {
  PhotoflipperModel,
  buildPhotosMediaPath,
  buildMediaCountsPath,
  GALLERY_PAGE_SIZE,
} from 'client/data/models/photoflipper';
import { getBreadcrumbSchema } from 'client/seo/schemas/breadcrumbs';
import { isUsed as isUsedPubState } from 'site-modules/shared/utils/publication-states';
import { buildDisplayVehicle } from 'site-modules/shared/utils/core-page/display-name-builders';
import { legacyPageUpdate } from 'client/actions/legacy-page';
import { PageModel } from 'client/data/models/page';
import { setAdsListToPreload } from 'site-modules/shared/components/native-ad/utils/utils';
import { BUILD_PRICE_AD } from 'site-modules/shared/components/native-ad/utils/constants';
import { ALL_CAT_NAME, PHOTO_TYPE } from 'site-modules/shared/constants/photoflipper/photoflipper';
import { getPicturesPageTitle } from 'site-modules/pictures/utils/pictures-seo-utils';
import { getPictureSrc } from 'site-modules/pictures/utils/gallery-utils';
import { getImageUrl, SOURCES } from 'client/utils/image-helpers';
import { PRELOADED_ASSETS_NUM } from 'site-modules/pictures/constants/pictures';

import { Pictures, PAGE_DISPLAY_NAME, URL_PATH } from './pictures';

export const pageOptions = {
  name: 'model_core_pictures',
  category: 'model_core',
  preload: (store, props) => {
    const params = props.match.params;
    const category = defaultTo(params.category, ALL_CAT_NAME);
    const { mobile } = store.getState();

    const preloader = new VehiclePreloader(store, { ...props, params });
    preloader.set('pageSize', PhotoflipperModel, GALLERY_PAGE_SIZE);

    return preloader.load().then(modelState => {
      const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);

      const isUsed = isUsedPubState(makeModelSubmodelYear.pubStates);
      store.dispatch(
        legacyPageUpdate({
          pageName: `${isUsed ? 'used' : 'new'}_model_core_pictures`,
          categoryName: `${isUsed ? 'used' : 'new'}_model_core`,
        })
      );

      preloader.resolve(buildMakeModelDefaultYear(params), VehicleModel);
      preloader.resolve(buildSubmodelsPath(params), VehicleGraphQLModel);
      preloader.resolve(buildMediaCountsPath({ ...params, mediaType: PHOTO_TYPE }), PhotoflipperModel);
      preloader.resolve(buildPhotosMediaPath({ ...params, category, pageNum: 1 }), PhotoflipperModel);
      preloader.resolve(getPageLinksPath(), LinkWidgetsModel);

      const vehiclePageContext = modelState.get('vehicle', PageModel);
      setAdsListToPreload(preloader, store.getState(), [BUILD_PRICE_AD.AD_NAME], vehiclePageContext);

      return preloader.load().then(modelStateInstance => {
        const photos = get(
          modelStateInstance.get(buildPhotosMediaPath({ ...params, category, pageNum: 1 }), PhotoflipperModel),
          'data',
          []
        );

        preloader.set(
          'server.preloadedAssets',
          PageModel,
          photos.slice(0, PRELOADED_ASSETS_NUM).map(({ src }) => ({
            href: getPictureSrc(getImageUrl(src, undefined, SOURCES.MEDIA), mobile),
            as: 'image',
          }))
        );

        return preloader.load();
      });
    });
  },
  seo: (state, props) => {
    const modelState = getModelState(state);
    const params = props.match.params;
    const category = defaultTo(params.category, ALL_CAT_NAME);
    const makeModelSubmodelYear = modelState.get(buildVehiclePath(params), VehicleModel);
    const defaultYear = modelState.get(buildMakeModelDefaultYear(params), VehicleModel);
    const mediaCounts = modelState.get(buildMediaCountsPath({ ...params, mediaType: PHOTO_TYPE }), PhotoflipperModel);
    const totalPhotos = get(mediaCounts, category, 0);
    const seoObject = {};

    if (makeModelSubmodelYear) {
      const displayVehicle = buildDisplayVehicle({ makeModelSubmodelYear, params, usedPrefix: '' });
      const { year } = params;
      const model = makeModelSubmodelYear.model.name;

      seoObject.jsonld = [
        getBreadcrumbSchema(
          buildCoreBreadcrumbs({
            makeModelSubmodelYear,
            params,
            defaultYear,
            pageDisplayName: PAGE_DISPLAY_NAME,
            urlPath: getUrlPathFromParams(params, URL_PATH),
            includeLastLink: true,
          })
        ),
      ];

      seoObject.title = getPicturesPageTitle({ displayVehicle, category, isMeta: true, totalPhotos });
      seoObject.description = `Edmunds has ${totalPhotos} pictures of the ${year} ${model} in our ${displayVehicle} photo gallery. Every Angle. Inside and Out. View all ${totalPhotos} pictures of the ${displayVehicle}, including hi-res images of the interior, exterior, dash, navigation system and tires.`;
      seoObject.canonical = `https://www.edmunds.com/${params.make}/${params.model}/${year}/pictures/`;
    }

    return seoObject;
  },
  component: props => <Pictures {...props} params={props.match.params} />,
  options: {
    hasVehicleContext: true,
  },
};

export default pageDefinition(pageOptions);
